import {Directive, ElementRef, forwardRef, HostListener, Input, OnChanges} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import {MAT_INPUT_VALUE_ACCESSOR} from '@angular/material/input';

export function percent(): ValidatorFn {
  return PercentageDirective.percent();
}
export function percentRequired(): ValidatorFn {
  return PercentageDirective.percentRequired();
}

@Directive({
  selector: 'input[digiresPercentage]',
  providers: [
    { provide: MAT_INPUT_VALUE_ACCESSOR, useExisting: PercentageDirective },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PercentageDirective),
      multi: true
    }
  ]
})
export class PercentageDirective implements ControlValueAccessor, OnChanges {

  @Input() decimalPlaces: number = 0;

  private _value: number | null;

  public static percentRequired(): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control || !control.value) {
        return {'missing': control.value};
      }
      return this.percentCheck(control);
    };
  }

  public static percent(): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control || !control.value) {
        return null;
      }
      return this.percentCheck(control);
    };
  }

  private static percentCheck(control: AbstractControl): ValidationErrors | null {
    if (typeof control.value === 'number') {
      return this.percentCheckNumber(control.value);
    } else if (typeof control.value === 'string') {
      return this.percentCheckString(control.value);
    }
    return {'NaN': control.value};
  }

  private static percentCheckNumber(value: any): ValidationErrors | null {
    if (value < 0 || value > 100) {
      return {'invalid': value};
    }
    return null;
  }

  private static percentCheckString(value: any): ValidationErrors | null {
    let asStr: string = value;
    if (asStr.endsWith('%')) {
      asStr = asStr.substring(0, asStr.length - 1);
    }
    if (/^\d+$/.test(asStr)) {
      const asNum = parseInt(asStr, 10);
      if (isNaN(asNum)) {
        return {'NaN': value};
      }
      if (asNum < 0 || asNum > 100) {
        return {'invalid': value};
      }
    } else {
      return {'NaN': value};
    }
    return null;
  }

  constructor(private element: ElementRef<HTMLInputElement>) {}

  // dummy function
  _onChange(_value: any): void {}

  registerOnChange(fn: (value: any) => void) {
    this._onChange = fn;
  }

  registerOnTouched() {}

  get value(): string | null {
    return this._value?.toFixed(0);
  }

  @Input()
  set value(value: string | null) {
    this.formatValue(value, true);
  }

  // called when the user finishes input
  @HostListener('change') ngOnChanges() {
    this.formatValue(this._value, true);
  }

  // called each time the user makes a partial input
  @HostListener('input', ['$event.target.value'])
  input(value) {
    this.formatValue(value, false);
  }

  // called when the value is changed through the form group
  writeValue(value: any) {
    this.formatValue(value, true);
  }

  // takes the string input and formats it nicely as a number. Resets input if it is not a number.
  private formatValue(value: any, updateNative: boolean) {
    if (value === null || value === undefined) {
      this._value = null;
      if (updateNative) {
        this.element.nativeElement.value = '';
      }
      return;
    }
    let asNumber;
    if (typeof value === 'number') {
      asNumber = Number(value);
    } else if (typeof value === 'string') {
      if (value.endsWith('%')) {
        value = value.substring(this.decimalPlaces, value.length - 1);
      }
      asNumber = parseFloat(value);
    } else {
      asNumber = NaN;
    }
    let asString;
    if (isNaN(asNumber)) {
      asString = '';
    } else {
      asString = asNumber.toFixed(this.decimalPlaces) + '%';
    }
    const previousValue: number = this._value;
    this._value = parseFloat(asNumber.toFixed(this.decimalPlaces));
    if (updateNative) {
      this.element.nativeElement.value = asString;
    } else {
      this.element.nativeElement.value = value;
    }
    if (previousValue !== asNumber) {
      if(Number.isNaN(this._value)) {
        this._value = null;
      }
      this._onChange(this._value);
    }
  }
}
